/**
 * Shapes for /ceo/emails and the customer panel's "Contact" modal.
 * Mirrors `services/email-campaigns/` in totalum-account-backend.
 */

export type CeoEmailMode = 'template' | 'raw';
export type CeoCampaignStatus = 'draft' | 'scheduling' | 'scheduled' | 'paused' | 'completed' | 'cancelled' | 'failed';
export type CeoFieldType = 'all' | 'enum' | 'number' | 'date' | 'text' | 'boolean';

/** A one-click From address. Any other local part on the domain is equally valid. */
export interface CeoSenderPresetI {
  email: string;
  name: string;
}

/** The closed list of mailboxes a reply may go to; `key: ''` means no Reply-To at all. */
export interface CeoReplyToOptionI {
  key: string;
  email: string;
  label: string;
}

export interface CeoAudienceFieldI {
  key: string;
  label: string;
  type: CeoFieldType;
  unit?: string;
  hint?: string;
  ops: { op: string; label: string }[];
  options?: { value: string; label: string }[];
}

export interface CeoAudienceConditionI {
  field: string;
  op: string;
  values?: string[];
  value?: string | number | null;
  value2?: string | number | null;
}

export interface CeoAudienceGroupI {
  combinator: 'and' | 'or';
  conditions: CeoAudienceConditionI[];
}

export interface CeoAudienceI {
  combinator: 'and' | 'or';
  groups: CeoAudienceGroupI[];
  includeUserIds: string[];
  excludeUserIds: string[];
  /** Campaigns whose recipients are skipped, whatever the rules say. */
  excludeCampaignIds: string[];
  /** The discard filter: anyone it matches is thrown out, even if added by hand. */
  excludeCombinator: 'and' | 'or';
  excludeGroups: CeoAudienceGroupI[];
}

/** Per past campaign, how many of its recipients were skipped — or why we cannot tell. */
export interface CeoExcludeCampaignInfoI {
  campaignId: string;
  name: string;
  sentAt: string | null;
  count: number;
  unavailable?: 'too_old' | 'not_sent' | 'permission' | 'error';
  detail?: string;
  truncated?: boolean;
}

export interface CeoEmailMessageI {
  mode: CeoEmailMode;
  /** Any local part on the Totalum domain. */
  fromEmail: string;
  fromName: string;
  /** '' = no reply-to; otherwise one of the allowed mailboxes. */
  replyToKey: string;
  subject: string;
  heading?: string;
  body: string;
  buttonLabel?: string;
  buttonUrl?: string;
}


export interface CeoEmailsMetaI {
  senderPresets: CeoSenderPresetI[];
  replyToOptions: CeoReplyToOptionI[];
  emailDomain: string;
  fields: CeoAudienceFieldI[];
  rateOptions: number[];
  defaultRate: number;
  maxRecipientsPerCampaign: number;
  maxScheduleHours: number;
  defaultTestEmail: string;
  aiEnabled: boolean;
}

/** SendGrid category stats for one campaign — aggregates only, never who received it. */
export interface CeoCampaignStatsI {
  requests: number;
  processed: number;
  delivered: number;
  deferred: number;
  bounces: number;
  blocks: number;
  drops: number;
  opens: number;
  uniqueOpens: number;
  clicks: number;
  uniqueClicks: number;
  spamReports: number;
  unsubscribes: number;
}

export interface CeoCampaignI {
  _id: string;
  name: string;
  status: CeoCampaignStatus;
  message: CeoEmailMessageI;
  audience: CeoAudienceI;
  ratePerHour: number;
  schedule?: {
    total: number;
    startAt: string;
    endAt: string;
    ratePerHour: number;
    scheduled: number;
    requests: number;
    batchId: string | null;
    canPause: boolean;
    note?: string | null;
  };
  stats?: CeoCampaignStatsI;
  statsUpdatedAt?: string;
  statsError?: string | null;
  failure?: string;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  pausedAt?: string;
  /** Why it is paused — your click, a SendGrid failure, or the health guard. */
  pauseReason?: string;
  /** The campaign paused itself on bad bounce/complaint numbers. */
  pausedByGuard?: boolean;
  cancelledAt?: string;
}

export interface CeoAudienceUserI {
  _id: string;
  name: string;
  email: string;
  firstName: string;
  language?: string;
  plan?: string;
  country?: string;
  continent?: string;
  channel?: string;
  projectsCount?: number;
  totalSpend?: number;
  createdAt?: string;
  manual?: boolean;
}

export interface CeoAudiencePreviewI {
  total: number;
  suppressed: {
    unsubscribed: number; account_closed: number; no_email: number; excluded: number;
    already_emailed: number; discard_filter: number; global_filter: number;
  };
  excludeCampaigns?: CeoExcludeCampaignInfoI[];
  users: CeoAudienceUserI[];
  page: number;
  limit: number;
  totalPages: number;
}

export interface CeoUserSearchResultI {
  _id: string;
  name: string;
  email: string;
  createdAt?: string;
  unsubscribed?: boolean;
  closed?: boolean;
  excluded?: boolean;
}

export interface CeoEmailPreviewI {
  subject: string;
  text: string;
  html: string | null;
  firstName: string;
  from: string;
  replyTo: string | null;
  to: string;
  recipientName: string;
}

export interface CeoQuickTemplateI {
  _id: string;
  name: string;
  subject: string;
  body: string;
  /** The sender this quick reply opens with; still changeable per message. */
  fromEmail?: string;
  fromName?: string;
  replyToKey?: string;
  updatedAt?: string;
}

export interface CeoExcludedUserI {
  _id: string;
  name: string;
  email: string;
  excludedAt?: string;
  createdAt?: string;
}

/** The warm-up answer for one From address: is this a normal volume for it? */
export interface CeoWarmupCheckI {
  allowance: number;
  busiestDay: number;
  firstTime: boolean;
  ok: boolean;
  unknown?: string;
}

export interface CeoPolishResultI {
  polishedText: string;
  language: string;
  unchanged: boolean;
}

export const EMPTY_AUDIENCE: CeoAudienceI = {
  combinator: 'and', groups: [], includeUserIds: [], excludeUserIds: [], excludeCampaignIds: [],
  excludeCombinator: 'and', excludeGroups: [],
};

/** The discard filter applied to every campaign, current and future. */
export interface CeoGlobalDiscardI {
  combinator: 'and' | 'or';
  groups: CeoAudienceGroupI[];
  updatedAt?: string;
  updatedBy?: string;
}

/** The two error shapes `SpHttpCommonService` produces — see the note in api-sdk.service. */
export function ceoEmailError(result: any, fallback: string): string {
  return result?.error?.errors || result?.error?.error?.errors || fallback;
}

export const NAME_VARIABLE = '{{name}}';

/** Per-browser memory. Every access is wrapped: blocked storage must never break the page. */
export const ceoEmailStorage = {
  get(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set(key: string, value: string): void {
    try { localStorage.setItem(key, value); } catch { /* storage blocked */ }
  },
};

export const STORAGE_KEYS = {
  lastQuickTemplate: 'ceoEmails.lastQuickTemplateId',
  lastSender: 'ceoEmails.lastSender',
  testEmail: 'ceoEmails.testEmail',
};
